import React from 'react';
import responsiveImage from '../assets/responsive.png';
import brandingImage from '../assets/brand.png';
import entertainmentImage from '../assets/entertainment.png';
import uiImage from '../assets/controls.png';

const translations = {
  de: {
    sectionTag: 'Features',
    title: 'Professionelles Scoreboard für moderne Rollhockey-Clubs',
    subtitle: 'Alles was du brauchst, um deine Spiele auf das nächste Level zu bringen. Von intelligenter Spielverwaltung mit automatischer Unterzahlzeit-Berechnung über flexibles Break-Management bis hin zu Entertainment-Features mit Pre-Game Videos und In-Game Musik. Im Browser ohne Installation — oder als Desktop-Edition, die alles davon automatisch abspielt.',
    feature1: {
      tag: 'Spielverwaltung',
      heading: 'Vollständige Spielverwaltung mit intelligenten Features',
      abstract: 'Steuere Timer, Scores, Teamfouls und Strafen mit einer intuitiven Oberfläche. Automatische Unterzahlzeit-Berechnung, intelligente Teamfoul-Markierungen und flexible Break-Verwaltung machen die Zeitnahme zum Kinderspiel.',
      highlights: [
        { label: 'Automatische Unterzahlzeit-Kumulierung', desc: 'Behalte den Überblick über Unterzahlsituationen. Zeiten werden automatisch berechnet und heruntergezählt – du musst nichts rechnen.' },
        { label: 'Intelligente Teamfoul-Markierungen', desc: 'Kritische Teamfouls werden automatisch farbig markiert. Immer perfekt sichtbar, auch bei rötlichen Vereinsfarben.' },
        { label: 'Flexible Break-Verwaltung', desc: 'Passe Break-Dauern an dein Turnier an. Automatisch oder manuell – du entscheidest.' },
      ]
    },
    feature2: {
      tag: 'Technologie',
      heading: 'Doppelscreen, responsiv & komplett webbasiert',
      abstract: 'Nutze die Anzeigetafel und Live-Ansicht auf jedem Gerät – überall. Nahtlose Erfahrung auf Laptop, Tablet und Smartphone. Mit nur einem Klick auf den zweiten Bildschirm erweitern.',
      highlights: [
        { label: 'Sofortige Doppelscreen-Unterstützung', desc: 'Ein Klick und die LiveView läuft auf Beamer oder TV. Keine komplizierte Einrichtung nötig.' },
        { label: 'Optimiert für alle modernen Browser', desc: 'Keine Installation nötig. Einfach URL öffnen und starten – auf jedem Gerät.' },
        { label: 'Echtzeit-Synchronisation', desc: 'Alle Bildschirme bleiben automatisch synchron. Änderungen erscheinen sofort überall.' },
      ]
    },
    feature3: {
      tag: 'Branding',
      heading: 'Mach es zu deinem – Vereinsbranding & Sponsoren',
      abstract: 'Passe Farben, Logo und Stil an deinen Club an. Füge bis zu 6 Sponsorenlogos hinzu, eröffne neue Einnahmequellen und erhöhe deine Professionalität mit einem automatischen Karussell.',
      highlights: [
        { label: 'Einfache Farb- und Logoeinstellung', desc: 'Vereinsfarbe wählen, Logo hochladen – fertig. Dein Scoreboard in Sekunden personalisiert.' },
        { label: 'Bis zu 6 Sponsorenlogos', desc: 'Präsentiere deine Sponsoren professionell. Automatisches Karussell für maximale Sichtbarkeit.' },
        { label: 'Professionelles Erscheinungsbild', desc: 'Beeindrucke Sponsoren und Zuschauer. Perfekt für Social Media und Livestreams.' },
      ]
    },
    feature4: {
      tag: 'Entertainment',
      heading: 'Pre-Game & In-Game Entertainment',
      abstract: 'Mach deine Halle zum Event! Vollständig integriertes Entertainment-System ohne manuelles Stoppen oder Nachdenken. Alles läuft automatisch – von Pre-Game Musik über Sponsor-Videos bis zur In-Game Musik.',
      highlights: [
        { label: 'Pre-Game Musik', desc: 'Musik läuft automatisch vor dem Spiel. Perfekte Atmosphäre ohne manuelles Eingreifen.' },
        { label: 'Sponsor-Videos & Fan-Unterhaltung', desc: 'Zeige Videos auf dem großen Bildschirm. Sponsorenpräsentationen und Fan-Entertainment in einem.' },
        { label: 'In-Game Musik-System', desc: 'Musik startet automatisch beim Spielbeginn. Pausiert bei Unterbrechungen – echtes Arena-Feeling.' },
      ]
    },
    feature5: {
      tag: 'Management',
      heading: 'Timeout & Penalty Management',
      abstract: 'Vollständige Verwaltung von Timeouts und Strafen mit visuellen Overlays. Buzzer-System mit einstellbarer Lautstärke für Spielende und kritische Teamfouls.',
      highlights: [
        { label: 'Visuelles Timeout-Overlay', desc: 'Zuschauer sehen sofort welches Team Timeout nimmt. Keine Verwirrung mehr.' },
        { label: 'Strafen-Verwaltung', desc: 'Strafen hinzufügen oder entfernen mit einem Klick. Läuft automatisch herunter.' },
        { label: 'Konfigurierbarer Buzzer', desc: 'Buzzer bei Spielende und kritischen Fouls. Lautstärke perfekt an deine Halle angepasst.' },
      ]
    },
  },
  en: {
    sectionTag: 'Features',
    title: 'Professional Scoreboard for Modern Rink Hockey Clubs',
    subtitle: 'Everything you need to take your games to the next level. From intelligent game management with automatic inferiority time calculation and flexible break management to entertainment features with pre-game videos and in-game music. In the browser with no installation — or as the desktop edition, which plays all of it automatically.',
    feature1: {
      tag: 'Game Management',
      heading: 'Complete Game Management with Intelligent Features',
      abstract: 'Control timer, scores, team fouls, and penalties with an intuitive interface. Automatic inferiority time calculation, intelligent team foul markers, and flexible break management make timekeeping a breeze.',
      highlights: [
        { label: 'Automatic Inferiority Time Accumulation', desc: 'Keep track of power play situations. Times calculated and counted down automatically – no math needed.' },
        { label: 'Intelligent Team Foul Markers', desc: 'Critical team fouls marked automatically in color. Always perfectly visible, even with reddish club colors.' },
        { label: 'Flexible Break Management', desc: 'Adapt break durations to your tournament. Automatic or manual – you decide.' },
      ]
    },
    feature2: {
      tag: 'Technology',
      heading: 'Dual Screen, Responsive & Purely Web-Based',
      abstract: 'Run your scoreboard and live view on any device, anywhere. Seamless experience on laptops, tablets, and phones. Extend to a second screen with zero fuss.',
      highlights: [
        { label: 'Instant Dual-Screen Support', desc: 'One click and LiveView runs on projector or TV. No complicated setup needed.' },
        { label: 'Optimized for All Modern Browsers', desc: 'No installation needed. Just open URL and start – on any device.' },
        { label: 'Real-Time Synchronization', desc: 'All screens stay automatically synchronized. Changes appear instantly everywhere.' },
      ]
    },
    feature3: {
      tag: 'Branding',
      heading: 'Make It Yours - Club Branding & Sponsors',
      abstract: 'Apply your club\'s colors, crest, and style. Add up to 6 sponsor logos, open up new revenue streams, and enhance your professional image with an automatic carousel.',
      highlights: [
        { label: 'Easy Color and Logo Customization', desc: 'Choose club color, upload logo – done. Your scoreboard personalized in seconds.' },
        { label: 'Up to 6 Sponsor Logos', desc: 'Present your sponsors professionally. Automatic carousel for maximum visibility.' },
        { label: 'Professional Appearance', desc: 'Impress sponsors and spectators. Perfect for social media and livestreams.' },
      ]
    },
    feature4: {
      tag: 'Entertainment',
      heading: 'Pre-Game & In-Game Entertainment',
      abstract: 'Transform your stadium into an event! Fully integrated entertainment system without manual stopping or thinking. Everything runs automatically – from pre-game music to sponsor videos to in-game music.',
      highlights: [
        { label: 'Pre-Game Music', desc: 'Music plays automatically before the game. Perfect atmosphere without manual intervention.' },
        { label: 'Sponsor Videos & Fan Entertainment', desc: 'Show videos on the big screen. Sponsor presentations and fan entertainment combined.' },
        { label: 'In-Game Music System', desc: 'Music starts automatically at game start. Pauses during interruptions – real arena feeling.' },
      ]
    },
    feature5: {
      tag: 'Management',
      heading: 'Timeout & Penalty Management',
      abstract: 'Complete management of timeouts and penalties with visual overlays. Buzzer system with adjustable volume for game end and critical team fouls.',
      highlights: [
        { label: 'Visual Timeout Overlay', desc: 'Spectators see immediately which team takes timeout. No more confusion.' },
        { label: 'Penalty Management', desc: 'Add or remove penalties with one click. Counts down automatically.' },
        { label: 'Configurable Buzzer', desc: 'Buzzer at game end and critical fouls. Volume perfectly adapted to your arena.' },
      ]
    },
  }
};

export default function FeatureSection({ lang = 'de' }) {
  const t = translations[lang];

  const features = [
    { data: t.feature1, image: uiImage, alt: 'Game management interface' },
    { data: t.feature2, image: responsiveImage, alt: 'Dual screen responsive layout' },
    { data: t.feature3, image: brandingImage, alt: 'Club branding and sponsors' },
    { data: t.feature4, image: entertainmentImage, alt: 'Entertainment system' },
    { data: t.feature5, image: uiImage, alt: 'Timeout and penalty management' },
  ];

  return (
    <div className="flex z-0 flex-col justify-center items-center px-8 py-32 w-full bg-white max-md:px-5 max-md:py-24 max-md:max-w-full">
      <div className="flex flex-col w-full max-w-[1200px] max-md:max-w-full">
        {/* Section Header */}
        <div className="flex flex-col max-w-[800px] w-full max-md:max-w-full mb-16 text-center mx-auto">
          <div className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">
            {t.sectionTag}
          </div>
          <div className="text-6xl font-extrabold leading-tight text-stone-900 max-md:max-w-full max-md:text-4xl mb-4">
            {t.title}
          </div>
          <div className="text-lg text-stone-600 max-md:max-w-full">
            {t.subtitle}
          </div>
        </div>

        {/* Feature Cards */}
        <div className="flex flex-col w-full max-md:max-w-full gap-20">
          {features.map((feature, index) => (
            <div key={index} className="flex flex-col gap-6">
              {/* Feature Header - Outside Box */}
              <div className="flex flex-col">
                {/* Feature Tag */}
                <div className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">
                  {feature.data.tag}
                </div>
                
                {/* Feature Heading */}
                <h2 className="text-4xl md:text-5xl font-bold mb-4 leading-tight text-stone-900">
                  {feature.data.heading}
                </h2>
                
                {/* Abstract */}
                <p className="text-lg text-stone-600 leading-relaxed max-w-3xl">
                  {feature.data.abstract}
                </p>
              </div>

              {/* Feature Content Box */}
              <div className="flex flex-col md:flex-row gap-0 rounded-2xl overflow-hidden shadow-lg">
                {/* Text Side - Dark Background */}
                <div className="flex-1 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-10 md:p-12 flex flex-col justify-center">
                  {/* Highlights */}
                  <div className="space-y-6">
                    {feature.data.highlights.map((highlight, hIndex) => (
                      <div key={hIndex} className="flex flex-col">
                        <div className="text-xl font-bold text-white mb-2">
                          {highlight.label}
                        </div>
                        <div className="text-base text-slate-300 leading-relaxed">
                          {highlight.desc}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Image Side - Light Background */}
                <div className="flex-1 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-8 md:p-12">
                  <img 
                    src={feature.image} 
                    alt={feature.alt} 
                    className="object-contain w-full max-w-2xl h-auto rounded-lg shadow-md"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}